import React from 'react';
import { connect } from 'react-redux';
import { State } from 'app/gamma/src/modules/types';
import { getMyTeams } from 'app/gamma/src/selectors/teams';
import {
  ExpandableNavigationItem,
  ExpandableNavigationItemLink,
  ExpandableNavigationItemIcon,
  ExpandableNavigationItemLogo,
  ExpandableNavigationItemText,
  ExpandableNavigationItemList,
  ExpandableNavigationItemButtonProps,
} from 'app/src/components/ExpandableNavigation';
import { TeamModel } from 'app/gamma/src/types/models';
import { TeamTabProps, TeamLinkSubTab } from './team-tab';
import { HomeTestIds } from '@trello/webdriverio-tests';
import { CreateTeamButton } from './create-team-button';
import { SectionHeader } from './section-header';
import { UpgradePromptFull } from 'app/src/components/UpgradePrompts';
import { useUpgradePrompt } from 'app/src/components/UpgradePrompts/useUpgradePrompt';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { IconName } from 'app/common/components/controls';

import styles from './team-list.less';

import { forTemplate, forNamespace } from 'app/common/lib/util/i18n';
const l = forTemplate('home');
const BC_PROMPT_MESSAGE_ID = 'ad-home-team-nav-tab';

const formatUpgradePrompt = forNamespace('upgrade prompt');

interface TeamTabListProps {
  teamTabs: TeamTabProps[];
  createTeamButton: ExpandableNavigationItemButtonProps;
}

interface TeamTabListStateProps {
  teams: TeamModel[];
}

interface TeamTabListDispatchProps {}

type AllTeamTabListProps = TeamTabListProps &
  TeamTabListStateProps &
  TeamTabListDispatchProps;

const TeamTab: React.FunctionComponent<TeamTabProps> = ({
  billingUrl,
  displayName,
  expanded,
  orgId,
  linkTabs,
  logoUrl1x,
  logoUrl2x,
  selected,
  ...props
}) => {
  const {
    dismissMessage,
    hidePrompt,
    shouldDisplayUpgradePrompt,
  } = useUpgradePrompt(orgId, BC_PROMPT_MESSAGE_ID);

  return (
    <ExpandableNavigationItem
      data-test-id={`${HomeTestIds.TeamTabSection}${orgId}`}
    >
      <ExpandableNavigationItemLink selected={selected} {...props}>
        {logoUrl1x && (
          <ExpandableNavigationItemLogo
            logoUrl1x={logoUrl1x}
            logoUrl2x={logoUrl2x}
          />
        )}
        {!logoUrl1x && <ExpandableNavigationItemIcon iconName="organization" />}
        <ExpandableNavigationItemText data-test-id={HomeTestIds.TeamTabName}>
          {displayName}
        </ExpandableNavigationItemText>
      </ExpandableNavigationItemLink>
      {expanded &&
        !!linkTabs.length && (
          <ExpandableNavigationItemList>
            {linkTabs.map((linkTab) => <TeamLinkSubTab {...linkTab} />)}
          </ExpandableNavigationItemList>
        )}
      {expanded &&
        shouldDisplayUpgradePrompt && (
          <UpgradePromptFull
            isDismissable
            headline={formatUpgradePrompt(['home', 'team tab', 'headline-2'])}
            content={formatUpgradePrompt(['home', 'team tab', 'content-2'])}
            iconName={IconName.BusinessClass}
            cta={formatUpgradePrompt('learn more')}
            ctaLink={billingUrl}
            onDismiss={() => {
              dismissMessage();
              hidePrompt();
            }}
            ctaTrackEvent={() =>
              trackUe({
                category: Noun.HOME_TEAM_ACCORDION,
                verb: Verb.OPENS,
                directObj: Noun.TEAM_PAGE_BC_TAB,
                method:
                  'by clicking full upgrade prompt in home page team accordion',
              })
            }
          />
        )}
    </ExpandableNavigationItem>
  );
};

const TeamTabListUnconntected: React.FunctionComponent<AllTeamTabListProps> = ({
  teamTabs,
  teams,
  createTeamButton,
}) => (
  <ExpandableNavigationItemList>
    <div className={styles.sectionHeaderWrapper}>
      <SectionHeader noMargin>{l('teams')}</SectionHeader>
      {teams.length > 0 && <CreateTeamButton {...createTeamButton} />}
    </div>
    {teamTabs.map((teamTab) => <TeamTab {...teamTab} />)}
    {teams.length === 0 && (
      <ExpandableNavigationItem>
        <CreateTeamButton fullTab {...createTeamButton} />
      </ExpandableNavigationItem>
    )}
  </ExpandableNavigationItemList>
);

const mapStateToProps = (state: State): TeamTabListStateProps => ({
  teams: getMyTeams(state),
});

export const TeamTabList = connect(mapStateToProps)(TeamTabListUnconntected);
